import { Injectable } from '@angular/core';
import { TrustedResourceService } from './trusted-resource.service';

@Injectable({
  providedIn: 'root'
})
export class DomSecurityHardeningService {
  private observer?: MutationObserver;

  constructor(private readonly trustedResourceService: TrustedResourceService) {}

  start(): void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    this.inspectTree(document.documentElement);

    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => this.inspectNode(node));
      }
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  private inspectTree(root: ParentNode | null): void {
    if (!root) {
      return;
    }

    this.inspectNode(root as unknown as Node);
    root.querySelectorAll('script, iframe, object, embed').forEach((element) => this.hardenElement(element));
  }

  private inspectNode(node: Node): void {
    if (!(node instanceof Element)) {
      return;
    }

    this.hardenElement(node);
    node.querySelectorAll('script, iframe, object, embed').forEach((element) => this.hardenElement(element));
  }

  private hardenElement(element: Element): void {
    switch (element.tagName) {
      case 'SCRIPT':
        this.hardenScript(element as HTMLScriptElement);
        break;
      case 'IFRAME':
        this.hardenFrame(element as HTMLIFrameElement);
        break;
      case 'OBJECT':
      case 'EMBED':
        this.hardenEmbeddedElement(element as HTMLEmbedElement | HTMLObjectElement);
        break;
      default:
        break;
    }
  }

  private hardenScript(script: HTMLScriptElement): void {
    const source = script.getAttribute('src') ?? '';
    if (!source || !this.trustedResourceService.isTrustedUrl(source, 'script', true)) {
      script.remove();
    }
  }

  private hardenFrame(frame: HTMLIFrameElement): void {
    if (frame.dataset.trustedFrame === 'true') {
      return;
    }

    const source = frame.getAttribute('src') ?? '';
    if (source && !this.trustedResourceService.isTrustedUrl(source, 'frame')) {
      frame.remove();
    }
  }

  private hardenEmbeddedElement(element: HTMLEmbedElement | HTMLObjectElement): void {
    const source = element.getAttribute('src') ?? element.getAttribute('data') ?? '';
    if (!source || !this.trustedResourceService.isTrustedUrl(source, 'frame')) {
      element.remove();
    }
  }
}
